/**
 * <mc-reallife>: "See it in real life" (snippets/mc-product-videos.liquid, source "social"), like the stories row of
 * revivalrugs.com. The tiles sit side by side across the column.
 * A tile opens the player docked bottom right (full screen on phones) and plays its clip right away, muted (browsers
 * block autoplay with sound), looping. CDN clips (data-src) play in a <video> with a progress line on top; YouTube
 * Shorts (data-youtube) are embedded only when played, scaled up so YouTube's own bar falls outside the frame.
 * Up/down (buttons, arrow keys, a vertical swipe) step through the clips, a click on the video pauses it, the sound button unmutes and the X or Escape closes it.
 */
class McReallife extends HTMLElement {
  connectedCallback() {
    document.addEventListener('keydown', this.onKeydown);
    if (this.player) return; // set up once, even if the element is moved
    this.clips = [...this.querySelectorAll('[data-clip]')];
    this.player = this.querySelector('[data-player]');
    this.stage = this.querySelector('[data-stage]');
    this.bar = this.querySelector('[data-progress]');
    this.count = this.querySelector('[data-count]');
    this.soundButton = this.querySelector('[data-sound]');
    this.sound = false;
    // The player moves to <body>: the info column is its own stacking layer, which would keep the fixed player
    // under the header and the chat bubble
    document.body.append(this.player);

    this.addEventListener('click', this.onClick);
    this.player.addEventListener('click', this.onClick);
    // Shorts without a portrait thumbnail fall back to the standard one
    this.querySelectorAll('[data-youtube] img').forEach((img) =>
      img.addEventListener('error', () => (img.src = img.src.replace('oardefault', 'mqdefault')), { once: true })
    );
    this.player.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.player.addEventListener('touchend', this.onTouchEnd);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    // The section re-renders on a variant change: this block's player goes with it (not when the block only moved)
    queueMicrotask(() => {
      if (this.isConnected) return;
      this.player.remove();
    });
  }

  onClick = (event) => {
    const target = event.target;
    const clip = target.closest('[data-clip]');
    const step = target.closest('[data-step]');
    if (clip) this.show(this.clips.indexOf(clip));
    else if (step) this.show(this.index + Number(step.dataset.step));
    else if (target.closest('[data-close]')) this.close();
    else if (target.closest('[data-sound]')) this.setSound(!this.sound);
    else if (target === this.video) this.video.paused ? this.video.play() : this.video.pause();
  };

  onKeydown = (event) => {
    if (this.player.hidden) return;
    if (event.key === 'Escape') this.close();
    else if (this.player.contains(document.activeElement) && ['ArrowUp', 'ArrowDown'].includes(event.key)) {
      event.preventDefault();
      this.show(this.index + (event.key === 'ArrowUp' ? -1 : 1));
    }
  };

  onTouchStart = (event) => {
    this.touchY = event.touches[0].clientY;
  };

  onTouchEnd = (event) => {
    const distance = event.changedTouches[0].clientY - this.touchY;
    if (Math.abs(distance) > 50) this.show(this.index + (distance < 0 ? 1 : -1));
  };

  onTime = () => {
    const { currentTime, duration } = this.video;
    this.bar.style.transform = `scaleX(${duration ? currentTime / duration : 0})`;
  };

  /* A <video> for a CDN clip, a muted embed for a Short (its sound is switched with postMessage) */
  createMedia(clip) {
    const id = clip.dataset.youtube;
    if (id) {
      const frame = document.createElement('iframe');
      frame.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&playsinline=1&rel=0&modestbranding=1&enablejsapi=1`;
      frame.allow = 'autoplay; encrypted-media; picture-in-picture';
      frame.title = clip.getAttribute('aria-label');
      // A regular (wide) video fills the player like its tile, cropped at the sides, not in a letterbox
      frame.classList.toggle('mc-pip__wide', !clip.hasAttribute('data-short'));
      frame.addEventListener('load', () => this.sound && this.setSound(true), { once: true });
      return frame;
    }
    const video = Object.assign(document.createElement('video'), { className: 'mc-pip__video', src: clip.dataset.src, loop: true, playsInline: true, muted: !this.sound });
    video.addEventListener('timeupdate', this.onTime);
    return video;
  }

  show(index) {
    const count = this.clips.length;
    this.index = (index + count) % count;
    const clip = this.clips[this.index];
    const media = this.createMedia(clip);
    this.video = media.tagName === 'VIDEO' ? media : null;
    this.bar.style.transform = 'scaleX(0)';
    this.count.textContent = `${this.index + 1} / ${count}`;
    this.bar.parentElement.hidden = !this.video;
    this.stage.querySelector('video')?.pause(); // a removed video would keep playing
    this.stage.replaceChildren(media);
    // A browser that refuses to play with sound still plays muted
    this.video?.play().catch(() => {
      this.setSound(false);
      this.video?.play().catch(() => {});
    });
    this.clips.forEach((button) => button.classList.toggle('is-playing', button === clip));
    if (this.player.hidden) {
      this.player.hidden = false;
      this.player.querySelector('[data-close]').focus({ preventScroll: true });
    }
  }

  setSound(on) {
    this.sound = on;
    this.soundButton.setAttribute('aria-pressed', String(on));
    const media = this.stage.firstElementChild;
    if (media?.tagName === 'VIDEO') media.muted = !on;
    else media?.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: on ? 'unMute' : 'mute', args: [] }), '*');
  }

  close() {
    this.player.hidden = true;
    this.stage.querySelector('video')?.pause();
    this.stage.replaceChildren();
    this.video = null;
    this.clips.forEach((button) => button.classList.remove('is-playing'));
    this.clips[this.index]?.focus({ preventScroll: true });
  }
}

if (!customElements.get('mc-reallife')) customElements.define('mc-reallife', McReallife);
